import type { ChangeEventHandler } from 'react'
import React, { useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import IconButton from '../IconButton/IconButton'
import ChevronLeft from '../icons/ChevronLeft'
import ChevronRight from '../icons/ChevronRight'
import Select from '../Select/Select'
import Typography from '../Typography/Typography'
import { ActionTypes } from './Actions'
import type { Limit } from './Table'
import { useTable } from './Table'

export type HandlesPaginationChange = (args: {
  limit: number
  page: number
}) => void

interface PaginationProps {
  onChange: HandlesPaginationChange
}

function Pagination({ onChange }: PaginationProps): JSX.Element {
  const intl = useIntl()
  const [state, dispatch] = useTable()

  const prevLimit = useRef(state.limit)
  const prevPage = useRef(state.page)

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    dispatch({
      type: ActionTypes.ChangeLimit,
      limit: Number(event.currentTarget.value) as Limit,
    })
  }

  const handleClickNext = () => {
    dispatch({ type: ActionTypes.NextPage })
  }

  const handleClickPrevious = () => {
    dispatch({ type: ActionTypes.PreviousPage })
  }

  useEffect(() => {
    if (prevLimit.current !== state.limit || prevPage.current !== state.page) {
      prevLimit.current = state.limit
      prevPage.current = state.page

      onChange({ limit: state.limit, page: state.page })
    }
  }, [onChange, state.limit, state.page])

  if (state.rows.length === 0) {
    return (
      <Typography component="p" variant="subtitle2">
        <FormattedMessage id="Ui.no_results" />
      </Typography>
    )
  }

  return (
    <div className="flex items-center">
      <nav
        aria-label={intl.formatMessage({ id: 'Table.pagination' })}
        className="flex items-center"
      >
        <Position />
        <ul className="flex items-center ml-4">
          <li className="inline-flex">
            <IconButton
              aria-label={intl.formatMessage({ id: 'Pagination.previous' })}
              disabled={state.page === 1}
              onClick={handleClickPrevious}
            >
              <ChevronLeft />
            </IconButton>
          </li>
          <li className="inline-flex ml-3">
            <IconButton
              aria-label={intl.formatMessage({ id: 'Pagination.next' })}
              disabled={state.page * state.limit >= state.total}
              onClick={handleClickNext}
            >
              <ChevronRight />
            </IconButton>
          </li>
        </ul>
      </nav>
      <div className="ml-6 min-w-40">
        <Select
          defaultValue={String(state.limit)}
          label={intl.formatMessage({ id: 'Table.per_page' })}
          onChange={handleChange}
          size="small"
        >
          <Select.Option value="25">25</Select.Option>
          <Select.Option value="50">50</Select.Option>
          <Select.Option value="100">100</Select.Option>
        </Select>
      </div>
    </div>
  )
}

function Position(): JSX.Element {
  const intl = useIntl()
  const [state] = useTable()
  const start = state.rows.length > 0 ? (state.page - 1) * state.limit + 1 : 0
  const end =
    start + state.limit - 1 > state.total
      ? state.total
      : state.rows.length > 0
      ? start + state.limit - 1
      : 0

  const message =
    start === 0 && end === 0
      ? intl.formatMessage(
          { id: 'Table.position_zero' },
          { total: state.total },
        )
      : intl.formatMessage(
          { id: 'Table.position' },
          { end, start, total: state.total },
        )

  return (
    <Typography component="p" variant="subtitle2">
      {message}
    </Typography>
  )
}

export default Pagination
